/**
 * Resilient clipboard utility across all modern and legacy browser environments.
 * Handles document focus loss, permission denial, iframes, and incognito restrictions.
 * Never throws an uncaught exception.
 *
 * @param {string} text - The text string to copy to the clipboard.
 * @returns {Promise<boolean>} - True if copied successfully, false otherwise.
 */
export async function safeCopyToClipboard(text) {
  if (!text) return false;

  // 1. Try modern navigator.clipboard API if available and document is focused
  if (typeof document !== "undefined" && typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    if (document.hasFocus()) {
      try {
        await navigator.clipboard.writeText(text);
        return true;
      } catch (err) {
        console.warn("navigator.clipboard.writeText failed, falling back to execCommand:", err);
      }
    }
  }

  // 2. Fallback to document.execCommand('copy') with a temporary textarea
  try {
    if (typeof document !== "undefined") {
      const textArea = document.createElement("textarea");
      textArea.value = text;
      // Position off-screen without causing page jump
      textArea.style.position = "fixed";
      textArea.style.top = "0";
      textArea.style.left = "-9999px";
      textArea.style.width = "2em";
      textArea.style.height = "2em";
      textArea.style.padding = "0";
      textArea.style.border = "none";
      textArea.style.outline = "none";
      textArea.style.boxShadow = "none";
      textArea.style.background = "transparent";
      textArea.setAttribute("readonly", "");
      document.body.appendChild(textArea);

      textArea.focus();
      textArea.select();
      textArea.setSelectionRange(0, text.length);

      const successful = document.execCommand("copy");
      document.body.removeChild(textArea);

      if (successful) return true;
    }
  } catch (fallbackErr) {
    console.warn("document.execCommand fallback failed:", fallbackErr);
  }

  // 3. Fallback: try navigator.clipboard as last resort if execCommand also failed
  if (typeof navigator !== "undefined" && navigator.clipboard?.writeText) {
    try {
      await navigator.clipboard.writeText(text);
      return true;
    } catch (_) {
      // Intentionally swallowed - return false so caller can provide user fallback
    }
  }

  return false;
}
